// Score-drivers strip on the summary page. One card per signal showing
// org-wide mean value, anchor comparison and a drill into the factor
// detail page.
//
// Calculations: reads ORG_FACTOR_STATS from riskScore.js. RAG colour
// derived from the comparison vs industry anchor (component / industry).
//
// Postgres mapping: `factor_stats` table (org_id, factor_id, mean,
// avg_contribution, users_above_industry). Refresh nightly.
// Integration hook: `useFactorStatsQuery()` cache 1h.
import React from 'react';
import { Box, Typography, Grid, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from 'motion/react';
import MainCard from '../../../../components/cards/MainCard';
import { ORG_FACTOR_STATS } from '../../riskScore';
import { useRiskBenchmarkQuery } from './BenchmarkPositionCard';
import useFeature from '../../useFeature';

// Renders the five risk-score factors as explorable cards. Replaces the old
// anonymous metric strip (sim click rate / course completion / engaged
// users), the spec wants Home to expose what's *driving* the score, not
// generic activity stats.

const fadeUp = { hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.04 } } };
// Maps a factor id to its averaged value on the `risk` aggregation payload.
const FACTOR_AGG_FIELD = { 'fail-rate': 'factorFailRate', 'repeat': 'factorRepeat', 'stage-depth': 'factorStageDepth', 'training-debt': 'factorTrainingDebt', 'course-lag': 'factorCourseLag' };
// Per-factor anchor fields on the risk_benchmark aggregation (raw 0-1 space,
// matching the anchors' normalised units).
const FACTOR_BENCH_FIELD = {
    'fail-rate':     { industry: 'industryFailRate',     platform: 'platformFailRate' },
    'repeat':        { industry: 'industryRepeat',       platform: 'platformRepeat' },
    'stage-depth':   { industry: 'industryStageDepth',   platform: 'platformStageDepth' },
    'training-debt': { industry: 'industryTrainingDebt', platform: 'platformTrainingDebt' },
    'course-lag':    { industry: 'industryCourseLag',    platform: 'platformCourseLag' },
};

// Both formatters work in normalised 0-1 space and denormalise on output.
// Stage depth was previously displayed as a raw decimal (e.g. "0.46") and
// course-lag was comparing normalised vs raw-day anchors; both produced
// confusing values. Everything is now on the same scale internally and only
// converts to the display unit at the very last moment.
function formatValue(v, anchor) {
    if (!anchor) return `${Math.round(v * 100)}%`;
    if (anchor.format === 'percent') return `${Math.round(v * 100)}%`;
    // 1.0 is the cap, not a measured average: show 30+d so a capped value
    // never reads as "exactly 30 days".
    if (anchor.format === 'days')    return v >= 1 ? '30+d' : `${Math.round(v * 30)}d`;
    return v.toString();
}

function formatAnchorValue(v, anchor) {
    if (!anchor) return `${Math.round(v * 100)}%`;
    if (anchor.format === 'percent') return `${Math.round(v * 100)}%`;
    if (anchor.format === 'days')    return `${Math.round(v * 30)}d`;
    return v.toString();
}

function FactorCard({ stat, drill, benchmarkOn }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { label, componentMean, anchor, meaning, soWhat, usersAbove, sampleSize, drill: target } = stat;
    // Grade against the licensed peer group: industry when benchmarking is
    // on, otherwise the platform baseline. Comfortably below = green, around
    // it = amber, meaningfully above = red. When there's no anchor we fall
    // back to the brand primary so colour isn't asserting a judgement we
    // can't back up.
    const baselineValue = anchor
        ? ((benchmarkOn ? (anchor.industry ?? anchor.platform) : (anchor.platform ?? anchor.industry)) ?? 0)
        : 0;
    const rag = (() => {
        if (!anchor || baselineValue === 0) return null;
        const ratio = componentMean / baselineValue;
        const goodBelow = anchor.goodBelow !== false;
        if (goodBelow) {
            if (ratio <= 0.85) return 'green';
            if (ratio <= 1.10) return 'amber';
            return 'red';
        }
        if (ratio >= 1.15) return 'green';
        if (ratio >= 0.90) return 'amber';
        return 'red';
    })();
    const color = rag === 'green' ? '#66BB6A'
                 : rag === 'amber' ? '#FFCA28'
                 : rag === 'red'   ? '#FF5252'
                 : theme.palette.primary.main;
    const railColor = color;
    const value = formatValue(componentMean, anchor);
    // When licensed we surface the gap against both the platform and the
    // industry anchor; without it the card stays unannotated and the RAG
    // colour carries the signal on its own.
    const gapLines = anchor && benchmarkOn
        ? [
            { label: 'platform', value: anchor.platform },
            { label: 'industry', value: anchor.industry },
        ]
            .filter((g) => g.value != null)
            .map((g) => {
                const mag = formatAnchorValue(Math.abs(componentMean - g.value), anchor);
                return `${mag} ${componentMean > g.value ? 'above' : 'below'} ${g.label}`;
            })
        : [];

    const handleExplore = () => {
        if (!target || !drill) return;
        // The drill descriptor carries an optional `value` (e.g. the
        // factor id for factor-detail). Pass it through as scope context
        // so the destination page can resolve which factor to render.
        if (target.value) {
            drill(target.page, target.label, { type: 'factor', value: target.value });
        } else {
            drill(target.page, target.label);
        }
    };

    return (
        <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <MainCard
                content={false}
                border={false}
                boxShadow
                sx={{ bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column' }}
            >
                <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%', gap: 1.25 }}>
                    {/* Header */}
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', lineHeight: 1.3 }}>
                        {label}
                    </Typography>

                    {/* Value */}
                    <Box>
                        <Typography sx={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color }}>
                            {value}
                        </Typography>
                        {meaning && (
                            <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.5, lineHeight: 1.4 }}>
                                {meaning}
                            </Typography>
                        )}
                        {gapLines.map((g, i) => (
                            <Typography key={g} sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary', mt: i === 0 ? 0.25 : 0 }}>
                                {g}
                            </Typography>
                        ))}
                    </Box>

                    {/* Anchor rail. Single tinted track with the You marker.
                        Static "targets" were removed: a customer's goal for
                        any of these factors is always "lower than now", so
                        a fixed target value is uninformative and visually
                        noisy. The gap text above carries the comparison
                        against industry when benchmarking is on. */}
                    {anchor && (() => {
                        const youPct  = Math.min(100, Math.max(0, componentMean * 100));
                        const zoneOpacity = isDark ? 0.32 : 0.22;
                        return (
                            <Box sx={{ position: 'relative', mt: 'auto' }}>
                                <Box sx={{
                                    position: 'relative',
                                    height: 18,
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: railColor,
                                    opacity: zoneOpacity,
                                }} />

                                {/* You marker */}
                                <Box sx={{
                                    position: 'absolute',
                                    left: `${youPct}%`,
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: 8,
                                    height: 32,
                                    bgcolor: color,
                                    borderRadius: '2px',
                                    boxShadow: isDark ? '0 0 0 2px rgba(0,0,0,0.5)' : '0 0 0 2px #fff',
                                    zIndex: 3,
                                    cursor: 'pointer',
                                }} />
                            </Box>
                        );
                    })()}

                    {/* Explore pinned to the bottom. Remediation copy lives on
                        the drill-in destination, not here, the card surfaces
                        the data, the destination surfaces what to do about it. */}
                    <Box sx={{ pt: 1.25 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={handleExplore}
                            disabled={!target}
                            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                            sx={{
                                alignSelf: 'flex-start',
                                borderRadius: '6px',
                                fontSize: 12,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                px: 1.75, py: '5px',
                                borderColor: 'secondary.main',
                                color: 'secondary.main',
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    borderColor: 'secondary.main',
                                    backgroundColor: 'secondary.main',
                                    color: 'common.white',
                                },
                                '& .MuiButton-endIcon': { transition: 'transform 200ms ease' },
                                '&:hover .MuiButton-endIcon': { transform: 'translateX(2px)' },
                            }}
                        >
                            More
                        </Button>
                    </Box>
                </Box>
            </MainCard>
        </motion.div>
    );
}

export default function FactorStrip({ drill, risk, scope }) {
    const benchmarkOn = useFeature('industryBenchmarking');
    // Cross-tenancy factor anchors: industry cohort average (3-peer floor,
    // null without an industry) with the platform average as fallback.
    // `scope` (incl. scope.tenancy) pools the selected child tenancies server
    // side; this card stays visible under scope, so the request must carry it.
    const benchQ = useRiskBenchmarkQuery(scope);
    const bench = benchQ.data?.riskBenchmark;
    const stats = ORG_FACTOR_STATS.map((s) => {
        const f = FACTOR_AGG_FIELD[s.id];
        const b = FACTOR_BENCH_FIELD[s.id];
        const next = { ...s };
        if (risk && f && risk[f] != null) next.componentMean = risk[f];
        if (bench && b) {
            next.anchor = {
                ...s.anchor,
                industry: bench[b.industry] ?? s.anchor?.industry ?? null,
                platform: bench[b.platform] ?? s.anchor?.platform ?? null,
            };
        }
        return next;
    });
    return (
        <motion.div variants={stagger} initial="hidden" animate="visible">
            <Box
                sx={{
                    display: 'grid',
                    gap: 1,
                    width: '100%',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        md: `repeat(${stats.length}, minmax(0, 1fr))`,
                    },
                    alignItems: 'stretch',
                }}
            >
                {stats.map((stat) => (
                    <Box key={stat.id} sx={{ minWidth: 0, '& > *': { width: '100%', height: '100%' } }}>
                        <FactorCard stat={stat} drill={drill} benchmarkOn={benchmarkOn} />
                    </Box>
                ))}
            </Box>
        </motion.div>
    );
}
